import React from 'react';

import type { PopoverPosition } from '@mui/material';
import { Divider, ListItemIcon, ListItemText, Menu, MenuItem } from '@mui/material';
import { ArrowLeftToLine, ArrowRightToLine, Pin, PinOff, SquareX, X } from 'lucide-react';
import { makeStyles } from 'tss-react/mui';

import type { Tab } from './tab-types';

const ICON_SIZE = 16;
const ICON_STROKE = 1.75;

const useStyles = makeStyles()(theme => ({
  icon: {
    /**
     * Compound selector matches specificity of the global theme override
     * ".MuiButtonBase-root-MuiMenuItem-root .MuiListItemIcon-root { min-width: 36 }"
     * so source order wins. 16px icon + ~6px breathing room to the label.
     */
    '&.MuiListItemIcon-root': {
      color: theme.appColors.iconsType,
      minWidth: 22,
    },
  },
}));

interface TabContextMenuProps {
  anchorPosition: PopoverPosition | null;
  onClose: () => void;
  onCloseOtherTabs: (tabId: UUID) => void;
  onCloseTab: (tabId: UUID) => void;
  onCloseTabsToLeft: (tabId: UUID) => void;
  onCloseTabsToRight: (tabId: UUID) => void;
  onTogglePin: (tabId: UUID) => void;
  tab: Tab | null;
  tabs: Tab[];
}

export function TabContextMenu({
  anchorPosition,
  onClose,
  onCloseOtherTabs,
  onCloseTab,
  onCloseTabsToLeft,
  onCloseTabsToRight,
  onTogglePin,
  tab,
  tabs,
}: TabContextMenuProps): JSX.Element | null {
  const { classes } = useStyles();

  if (!tab) return null;

  const tabIndex = tabs.findIndex(t => t.id === tab.id);
  const unpinnedOthers = tabs.filter(t => t.id !== tab.id && !t.pinned);
  const unpinnedToLeft = tabs.filter((t, i) => i < tabIndex && !t.pinned);
  const unpinnedToRight = tabs.filter((t, i) => i > tabIndex && !t.pinned);

  return (
    <Menu
      anchorPosition={anchorPosition ?? undefined}
      anchorReference="anchorPosition"
      open={anchorPosition !== null}
      slotProps={{ backdrop: { invisible: true } }}
      onClose={onClose}
    >
      <MenuItem
        dense
        disabled={tab.pinned}
        onClick={() => {
          onCloseTab(tab.id);
          onClose();
        }}
      >
        <ListItemIcon className={classes.icon}>
          <X size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </ListItemIcon>
        <ListItemText>Close</ListItemText>
      </MenuItem>
      <MenuItem
        dense
        disabled={unpinnedOthers.length === 0}
        onClick={() => {
          onCloseOtherTabs(tab.id);
          onClose();
        }}
      >
        <ListItemIcon className={classes.icon}>
          <SquareX size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </ListItemIcon>
        <ListItemText>Close Others</ListItemText>
      </MenuItem>
      <MenuItem
        dense
        disabled={unpinnedToLeft.length === 0}
        onClick={() => {
          onCloseTabsToLeft(tab.id);
          onClose();
        }}
      >
        <ListItemIcon className={classes.icon}>
          <ArrowLeftToLine size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </ListItemIcon>
        <ListItemText>Close All to Left</ListItemText>
      </MenuItem>
      <MenuItem
        dense
        disabled={unpinnedToRight.length === 0}
        onClick={() => {
          onCloseTabsToRight(tab.id);
          onClose();
        }}
      >
        <ListItemIcon className={classes.icon}>
          <ArrowRightToLine size={ICON_SIZE} strokeWidth={ICON_STROKE} />
        </ListItemIcon>
        <ListItemText>Close All to Right</ListItemText>
      </MenuItem>
      <Divider />
      <MenuItem
        dense
        onClick={() => {
          onTogglePin(tab.id);
          onClose();
        }}
      >
        <ListItemIcon className={classes.icon}>
          {tab.pinned ? (
            <PinOff size={ICON_SIZE} strokeWidth={ICON_STROKE} />
          ) : (
            <Pin size={ICON_SIZE} strokeWidth={ICON_STROKE} />
          )}
        </ListItemIcon>
        <ListItemText>{tab.pinned ? 'Unpin Tab' : 'Pin Tab'}</ListItemText>
      </MenuItem>
    </Menu>
  );
}
